import classNames from "classnames";
import * as React from "react";
import styled, { css } from "styled-components";
import { DropdownCellProps, DropdownMenu, TableDropdownCell } from "../DropdownMenu";
import { Icon } from "../Icon";
import { Link } from "../Link";
import { Paginator, PaginationProps, PaginatorClickEventProps } from "../Paginator";
import { Text } from "../Text";
import { useToggle } from "../hooks/useToggle";
import { mediaQueries } from "../style";
import { MediaQueryWrapper, style } from "../style";
import { focusOutline } from "../utils/focusOutlineStyle";
import { TableError, TableErrorProps } from "./Error";
import { Head, Header, HeaderProps, makeHeaders, TableHeader } from "./Headers";

const MOBILE_HEADER_WIDTH = "130px";

type ItemType = {
  [key: string]: any;
  disabled?: boolean;
};

export type Props = {
  className?: string;
  config?: TableConfigProps;
  items?: ItemType[];
  small?: boolean;
  medium?: boolean;
  large?: boolean;
  mobileFirstColumnOnly?: boolean;
  firstColumnOnlyBreakpoint?: string | MediaQueryWrapper;
  error?: TableErrorProps;
  noDropdown?: boolean;
  isChildTable?: boolean;
  isParentTable?: boolean;
  isSingleNestedLayout?: boolean;
  noHeader?: boolean;
};

export type TableConfigProps = {
  columns: ConfigColumnProps[];
  menuItems?: MenuItem[] | ((item: any, index: number) => MenuItem[]);
  renderMenu?: (item: any, index: number) => React.ReactNode;
  mobileMenuItemTitle?: string | ((item: any) => string);
  title?: string;
  pagination?: PaginationConfigProps;
  nestedLayout?: string;
};

export type PaginationConfigProps = PaginationProps &
  PaginatorClickEventProps & {
    disabled?: boolean;
    loading?: boolean;
    inlinePaginator?: boolean;
  };

type MenuItem = {
  title: string;
  action: (close: () => void) => void;
  danger?: boolean;
};

export type ConfigColumnProps = {
  header: HeaderProps;
  flex?: number;
  render?: (item: any) => any;
};

type RowProps = {
  children?: React.ReactNode;
  isChildTable?: boolean;
  isExpanded?: boolean;
  isExpandedByDefault?: boolean;
  hasSingleNestedTable?: boolean;
  mobileFirstColumnOnly?: boolean;
  firstColumnOnlyBreakpoint?: string | MediaQueryWrapper;
  disabled?: boolean;
};

type CellProps = {
  children?: React.ReactNode;
  label?: string;
  flex?: number | string;
  showExpandableIcon?: boolean;
  expandDisabled?: boolean;
};

const TABLE_BORDER_STYLE = `${style.vars.borderWidth.base} solid ${style.colors.grey.light}`;
const ROW_HEIGHT_SMALL = "48px";
const ROW_HEIGHT_MED = "56px";
const ROW_HEIGHT_LARGE = "72px";

const CellWrapper = styled.div<CellProps>`
  display: flex;
  flex-wrap: wrap;
  flex: auto;
  align-items: center;
  max-width: 100%;
`;

const TableContainer = styled.div<Props>`
  flex: 100%;
  max-width: 100%;

  ${({ isChildTable }) =>
    isChildTable &&
    css`
      padding: 0 ${style.vars.space["3"]};
      margin-top: ${style.vars.space["5"]};
    `}
`;

const NestedContentContainer = styled.div<{ $borderless?: boolean }>`
  flex: 100%;
  padding: 0 ${style.vars.space["3"]};
  border-top: ${(props) => (props.$borderless ? "none" : TABLE_BORDER_STYLE)};
  margin-top: -2px;
`;

const TableCell = styled.div<CellProps>`
  display: flex;
  align-items: center;
  padding: ${style.vars.space["1"]} ${style.vars.space["3"]};
  flex: ${({ flex }) => (flex ? flex : 1)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .is-small & {
    max-height: ${ROW_HEIGHT_SMALL};
  }

  .is-medium & {
    max-height: ${ROW_HEIGHT_MED};
    padding: ${style.vars.space["2"]} ${style.vars.space["3"]};
  }

  .is-large & {
    max-height: ${ROW_HEIGHT_LARGE};
    padding: 0.75rem ${style.vars.space["3"]};
  }
`;

const TableRow: any = styled.div<RowProps>`
  position: relative;
  display: flex;
  align-items: center;
  background-color: ${style.colors.white};
  flex: auto;

  border-left: ${TABLE_BORDER_STYLE};
  border-right: ${TABLE_BORDER_STYLE};
  border-bottom: ${TABLE_BORDER_STYLE};

  &:last-of-type {
    border-bottom-left-radius: ${style.vars.borderRadius.base};
    border-bottom-right-radius: ${style.vars.borderRadius.base};
  }

  ${({ firstColumnOnlyBreakpoint, mobileFirstColumnOnly, isExpanded }) => css`
    ${isExpanded
      ? css`
          ${CellWrapper} > ${TableContainer} {
            border-top: ${TABLE_BORDER_STYLE};
            margin-top: -2px;
          }

          .is-small &&,
          .is-medium &&,
          .is-large && {
            height: initial;
          }
        `
      : css`
          border-top: 2px solid transparent;

          ${CellWrapper} > ${TableContainer} {
            border-top: none;
            margin-top: 0;
          }

          .is-small > & {
            height: ${ROW_HEIGHT_SMALL};
          }

          .is-medium > & {
            height: ${ROW_HEIGHT_MED};
          }

          .is-large > & {
            height: ${ROW_HEIGHT_LARGE};
          }
        `}
    ${firstColumnOnlyBreakpoint} {
      ${mobileFirstColumnOnly
        ? css`
            ${TableCell} {
              .is-small > & {
                height: ${ROW_HEIGHT_SMALL};
              }
              .is-medium > & {
                height: ${ROW_HEIGHT_MED};
              }
              .is-large > & {
                height: ${ROW_HEIGHT_LARGE};
              }
            }
          `
        : css`
            .is-small &,
            .is-medium &,
            .is-large & {
              height: initial;
            }
            border-top: none;
            ${CellWrapper} > ${TableContainer} {
              margin-top: 0;
            }
          `}
    }
  `}

  ${({ hasSingleNestedTable }) =>
    hasSingleNestedTable &&
    css`
      & ${TableRow} {
        border-left: none;
        border-right: none;
      }
    `};

  ${({ isExpanded, hasSingleNestedTable }) =>
    isExpanded &&
    hasSingleNestedTable &&
    css`
      border-bottom: none;
      margin: ${style.vars.space["3"]} 0;

      &:not(:nth-of-type(2)) {
        border-top: ${TABLE_BORDER_STYLE};
        border-radius: ${style.vars.borderRadius.base};
      }

      & + & {
        border-top: ${TABLE_BORDER_STYLE};
        border-radius: ${style.vars.borderRadius.base};
      }

      &:nth-of-type(2) {
        margin-top: 0;
      }
    `};

  ${TableContainer} {
    display: ${({ isExpanded }) => (isExpanded ? "block" : "none")};
  }

  /* Interpolation tagging to resolve stylelint error */
  /* https://www.styled-components.com/docs/tooling#interpolation-tagging */
  ${/* sc-selector */ Head} & {
    background-color: ${style.colors.grey.lightest};
    border: none;
    height: initial;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      & ${CellWrapper} {
        pointer-events: none;
        opacity: ${style.vars.disabledOpacity};
      }
    `};
`;

export const TableElt = styled.div<Props>`
  border-radius: ${style.vars.borderRadius.base};

  ${({ firstColumnOnlyBreakpoint }) => firstColumnOnlyBreakpoint} {
    &.is-medium ${/* sc-selector */ TableHeader}, &.is-large ${TableHeader} {
      height: ${style.vars.height.base};
    }

    &.is-medium ${TableHeader}:nth-of-type(n + 2),
    &.is-large ${TableHeader}:nth-of-type(n + 2) {
      display: none;
    }

    ${TableRow} ${TableCell} {
      flex: 100%;
    }

    &.with-dropdown {
      ${TableRow} ${TableCell} {
        margin-right: 2.5rem;
      }
    }

    ${({ mobileFirstColumnOnly }) =>
      mobileFirstColumnOnly
        ? css`
            ${TableRow} ${TableCell} {
              /* Hide all columns and just display the first */
              &:nth-of-type(n + 2) {
                display: none;
              }
            }
          `
        : css`
            /* If not using mobileFirstColumnOnly, show the header label on all cells */
            ${TableRow} ${TableCell} {
              &:before {
                font-weight: ${style.vars.fontWeight.bold};
                margin-right: ${style.vars.space["4"]};
                min-width: ${MOBILE_HEADER_WIDTH};
                max-width: ${MOBILE_HEADER_WIDTH};
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
              }

              /* dont show the first column */
              &:nth-of-type(n + 2):before {
                content: attr(data-label);
              }
            }

            /* On small version of mobile, include the first column and make all the header cols left aligned w/ a grey bg */
            &.is-small ${TableRow} ${TableCell} {
              padding: 0;

              &:before {
                content: attr(data-label);
                padding: ${style.vars.space["1"]} ${style.vars.space["3"]};
                background-color: ${style.colors.grey.lightest};
                border-right: ${TABLE_BORDER_STYLE};
              }
            }
          `}
  }

  ${({ noHeader }) =>
    noHeader &&
    css`
      border-top: ${TABLE_BORDER_STYLE};
    `}

  ${({ isChildTable, firstColumnOnlyBreakpoint, mobileFirstColumnOnly }) =>
    isChildTable
      ? css`
          &:last-of-type {
            margin-bottom: ${style.vars.space["3"]};
          }

          ${firstColumnOnlyBreakpoint} {
            ${mobileFirstColumnOnly &&
            css`
              &.is-small {
                border-top: ${TABLE_BORDER_STYLE};
              }
            `}
          }
        `
      : css`
          ${firstColumnOnlyBreakpoint} {
            &.is-small {
              border-top: ${TABLE_BORDER_STYLE};
            }
          }
        `}
`;

const TitleContainer = styled.div<Props>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: ${style.vars.space["2"]};

  ${({ isSingleNestedLayout }) =>
    isSingleNestedLayout &&
    css`
      margin-top: ${style.vars.space["2"]};
      margin-left: ${style.vars.space["2"]};
    `};
`;

const Title = styled(Text).attrs({ type: "h5" })`
  margin-top: 0;
  margin-bottom: 0;
  /* Indent to account for rounded corner on table below */
  margin-left: ${style.vars.borderRadius.base};
`;

const DefaultPaginator = styled(Paginator)`
  margin-top: ${style.vars.space["3"]};
  text-align: center;
  width: 100%;
`;

const InlinePaginator = styled(Paginator)``;

const IconBorder = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${style.colors.grey.dark};
  height: ${style.vars.space["3"]};
  width: ${style.vars.space["3"]};
  border: ${style.vars.borderWidth.thin} solid ${style.colors.grey.dark};
  border-radius: ${style.vars.borderRadius.base};
`;

// This needs to be a separate element from ExpandIconWrapper, otherwise
// `pointer-events: none` prevents the cursor from changing.
export const ExpandIconDisabled = styled.div<CellProps>`
  ${({ expandDisabled }) =>
    expandDisabled &&
    css`
      cursor: not-allowed;
    `}
`;

export const ExpandIconWrapper = styled.button.attrs({
  type: "button",
})<CellProps>`
  all: unset;
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${style.vars.space["6"]};
  width: ${style.vars.space["6"]};
  min-width: ${style.vars.space["6"]}; /* Prevents width collapsing in certain cases */
  cursor: pointer;
  border: none;
  border-radius: ${style.vars.borderRadius.base};
  margin-left: -12px;
  margin-right: ${style.vars.space["2"]};
  padding: 0;

  background-color: ${style.colors.white};

  &:focus {
    ${focusOutline()};
  }

  &:hover {
    background-color: ${style.colors.grey.lightest};
  }

  &:active {
    background-color: ${style.colors.grey.light};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.3;
      pointer-events: none;
    `}
`;

export const CellTextWrapper = styled.div`
  padding: ${style.vars.whitespace.xsmall};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CellSubText = styled.small`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CellLink = styled(Link)`
  display: inline-block;
  overflow: hidden;
  padding-left: ${style.vars.whitespace.xsmall};
  padding-right: ${style.vars.whitespace.xsmall};
  text-overflow: ellipsis;

  &&:active:after,
  &&:focus:after {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
`;

export const ExternalCellLink = styled(CellLink).attrs({ type: "external" })``;

export const RowContext = React.createContext<[boolean, (next?: boolean) => void]>([false, () => null]);

export const Row: React.FC<RowProps> = ({ children, ...props }) => {
  const [isExpanded, toggleExpanded] = useToggle(!!props.isExpandedByDefault);

  return (
    <RowContext.Provider value={[isExpanded, toggleExpanded]}>
      <TableRow {...props} isExpanded={isExpanded}>
        <CellWrapper>{children}</CellWrapper>
      </TableRow>
    </RowContext.Provider>
  );
};

export const Cell: React.FC<CellProps> = ({ label, showExpandableIcon, expandDisabled, children, ...props }) => {
  const [isExpanded, toggleExpanded] = React.useContext(RowContext);

  return (
    <TableCell {...props} data-label={label}>
      {showExpandableIcon && (
        <ExpandIconDisabled expandDisabled={expandDisabled}>
          <ExpandIconWrapper
            onClick={() => toggleExpanded()}
            disabled={expandDisabled}
            aria-label="Expand table row button"
            aria-expanded={isExpanded}
            tabIndex={expandDisabled ? -1 : 0}
          >
            <IconBorder>
              <Icon small icon={isExpanded ? "minus" : "plus"} />
            </IconBorder>
          </ExpandIconWrapper>
        </ExpandIconDisabled>
      )}
      {children}
    </TableCell>
  );
};

export const DropdownCell: React.FC<DropdownCellProps> = ({ children, firstColumnOnlyBreakpoint = mediaQueries.mediumMax, ...props }) => {
  const [isExpanded] = React.useContext(RowContext);
  return (
    <TableDropdownCell {...props} isExpanded={isExpanded} firstColumnOnlyBreakpoint={firstColumnOnlyBreakpoint}>
      {children}
    </TableDropdownCell>
  );
};

interface NestedContentProps {
  borderless?: boolean;
  content: any;
}
export const NestedContent: React.FC<NestedContentProps> = ({ borderless, content }) => {
  const [isExpanded] = React.useContext(RowContext);
  return isExpanded ? <NestedContentContainer $borderless={borderless}>{content}</NestedContentContainer> : null;
};

export const Table: React.FC<Props> = ({
  children,
  small,
  medium,
  large,
  className,
  mobileFirstColumnOnly,
  error,
  noDropdown,
  config,
  items,
  isChildTable,
  isSingleNestedLayout,
  noHeader,
  firstColumnOnlyBreakpoint = mediaQueries.mediumMax,
}) => {
  const shouldGenerate = items && config;
  const hasMenuItems = (shouldGenerate && (config?.renderMenu || config?.menuItems)) || (!shouldGenerate && !noDropdown);
  const classes = classNames({
    [className ?? ""]: className,
    "is-small": small || (!medium && !large),
    "is-medium": medium,
    "is-large": large,
    "with-dropdown": hasMenuItems,
  });

  const headersFromConfig = config
    ? config.columns.map(({ header, flex }) => {
        header.flex = flex;
        return header;
      })
    : null;

  const renderersFromConfig = config ? config.columns.map(({ render }) => render) : [];

  const [headersData, setHeaders] = React.useState(headersFromConfig);
  const [childrenToDisplay, setChildrenToDisplay] = React.useState(children);
  const [isParentTable, setIsParentTable] = React.useState(false);

  const tableHeaders = headersData && !noHeader ? makeHeaders(headersData, setHeaders, !hasMenuItems, firstColumnOnlyBreakpoint) : null;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const showDefaultPaginator =
    config &&
    config?.pagination &&
    config?.pagination?.pages > 1 &&
    !config?.pagination?.inlinePaginator &&
    !isChildTable &&
    !isSingleNestedLayout;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const showInlinePaginator =
    config && config?.pagination && config?.pagination?.pages > 1 && (config?.pagination?.inlinePaginator || isChildTable);

  // Generate table rows from items if passed in
  const generateTableRows = (headerTitles: string[]): JSX.Element[] => {
    if (!items) {
      return [];
    }

    return items.map((item, i) => {
      const rowItems =
        config?.columns.map((column, j) => {
          return (
            <Cell
              key={`cell-${i}-${j}`}
              flex={column.flex}
              label={headerTitles[j]}
              showExpandableIcon={(item.nestedAsyncContent || item.nestedTables) && j === 0}
              expandDisabled={item.expandDisabled}
            >
              {renderersFromConfig[j]?.(item)}
            </Cell>
          );
        }) ?? [];

      if (hasMenuItems && config?.menuItems) {
        const menuItems = typeof config?.menuItems === "function" ? config?.menuItems?.(item, rowItems.length) : config?.menuItems;

        const menuTitle = typeof config.mobileMenuItemTitle === "function" ? config.mobileMenuItemTitle(item) : config.mobileMenuItemTitle;

        rowItems.push(
          <DropdownCell
            key={`dropdown-${i}`}
            firstColumnOnlyBreakpoint={firstColumnOnlyBreakpoint}
            mobileFirstColumnOnly={mobileFirstColumnOnly}
          >
            <DropdownMenu mobileTitle={menuTitle} menuItems={menuItems} data-testid="row-dropdown-menu" />
          </DropdownCell>
        );
      }

      if (hasMenuItems && config?.renderMenu) {
        rowItems.push(
          <DropdownCell
            key={`dropdown-${i}`}
            firstColumnOnlyBreakpoint={firstColumnOnlyBreakpoint}
            mobileFirstColumnOnly={mobileFirstColumnOnly}
          >
            {config?.renderMenu?.(item, i)}
          </DropdownCell>
        );
      }

      if (item.nestedTables) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const singleNestedLayout = config?.nestedLayout === "single";

        const childTables = item.nestedTables.map((table: any, k: number) => {
          return (
            <Table
              small={singleNestedLayout}
              medium={medium && !singleNestedLayout}
              large={large && !singleNestedLayout}
              key={`${table.config.title}-${k}`}
              config={table.config}
              items={table.items}
              noHeader={singleNestedLayout}
              isSingleNestedLayout={singleNestedLayout}
              mobileFirstColumnOnly={mobileFirstColumnOnly}
              firstColumnOnlyBreakpoint={firstColumnOnlyBreakpoint}
            />
          );
        });

        if (singleNestedLayout) {
          rowItems.push(childTables[0]);
        } else {
          rowItems.push(
            <Table
              key={`nested-table-${i}`}
              medium={medium}
              large={large}
              mobileFirstColumnOnly={mobileFirstColumnOnly}
              firstColumnOnlyBreakpoint={firstColumnOnlyBreakpoint}
            >
              {childTables}
            </Table>
          );
        }
      }

      if (item.nestedAsyncContent) {
        rowItems.push(<NestedContent key={`nested-content-${i}`} content={item.nestedAsyncContent} />);
      }

      const lastCell = rowItems[rowItems.length - 1];

      // Create unique key per row for pagination,
      // otherwise the key is the same for each row for each page
      // This allows item.isExpandedByDefault to be recognized as
      // a user navigates between pages
      const key = `row-${i}-${Date.now()}`;

      return (
        <Row
          key={key}
          data-testid={`row-${i}`}
          hasSingleNestedTable={lastCell.props.isSingleNestedLayout}
          firstColumnOnlyBreakpoint={firstColumnOnlyBreakpoint}
          mobileFirstColumnOnly={mobileFirstColumnOnly}
          isExpandedByDefault={!!item.isExpandedByDefault}
          disabled={item.disabled}
        >
          {rowItems}
        </Row>
      );
    });
  };

  // Allow tables created with markup to mirror the functionality of generated tables
  React.useEffect(() => {
    let generatedHeaderProps: HeaderProps[] = [];
    if (shouldGenerate) {
      setChildrenToDisplay(generateTableRows(headersData?.map(({ title }) => title ?? "") ?? []));
    } else if (error) {
      // Display empty row for error if there are no items
      setChildrenToDisplay(<Row></Row>);
    } else {
      // This table was created with markup
      let headerTitles: any[];

      setChildrenToDisplay(
        React.Children.map(children, (row) => {
          /**
           * Necessary to make types check
           */
          if (!React.isValidElement(row)) {
            return null;
          }

          // Remove the <Head> row, we'll generate it instead
          if (row.type === Head) {
            // There should only be one <Row> in the <Head>, but we need to React.Children.forEach anyway
            React.Children.forEach(row.props.children, (headerRow: React.ReactElement) => {
              const cells = headerRow.props.children;
              headerTitles = [];

              generatedHeaderProps = React.Children.map(cells, (cell, i) => {
                // Ignore the menuItem <Header> cell
                if (hasMenuItems && i >= cells.length - 1) {
                  return;
                }
                // Add the text in the <Header> to the headerTitles array for mobile view
                headerTitles.push(cell.type === Header && typeof cell.props.children === "string" ? cell.props.children : "");
                // Finally, map it into a HeaderProp
                return {
                  ...cell.props,
                  title: cell.props.children,
                };
              });
            });

            // Remove the <Head> row, it will be generated from generatedHeaderProps instead
            return null;
          }

          // Add isChildTable prop to child tables
          if (row.type === Table) {
            setIsParentTable(true);

            return React.cloneElement(row, {
              isChildTable: true,
            } as any);
          }

          // No need to add a label if we don't know the headers or this isn't a <Row>
          if (!headerTitles || row.type !== Row) {
            return row;
          }

          // Add a label prop to each Cell so we can show as a Header it in mobile view
          let cellCount = 0;
          return React.cloneElement(row, {
            firstColumnOnlyBreakpoint,
            mobileFirstColumnOnly,
            children: React.Children.map(row.props.children, (cell: React.ReactElement<CellProps>) => {
              if (cell.type !== Cell) {
                return cell;
              }

              return React.cloneElement(cell, {
                label: headerTitles[cellCount++],
              });
            }),
          } as any);
        })
      );

      if (!noHeader) {
        setHeaders(generatedHeaderProps);
      }
    }
  }, [children, items, mobileFirstColumnOnly]);

  // Render when there are children to display to prevent from flickering on first load
  return childrenToDisplay ? (
    <TableContainer isChildTable={isChildTable}>
      {(showInlinePaginator || (config && config.title)) && (
        <TitleContainer isChildTable={isChildTable} isSingleNestedLayout={isSingleNestedLayout}>
          <Title>{config?.title}</Title>
          {showInlinePaginator && (
            <InlinePaginator
              inline
              disabled={config?.pagination?.disabled}
              loading={config?.pagination?.loading}
              pagination={
                config?.pagination ?? {
                  page: 0,
                  pages: 0,
                  per_page: 0,
                }
              }
              fetchMore={config?.pagination?.fetchMore}
              onPrevious={config?.pagination?.onPrevious}
              onNext={config?.pagination?.onNext}
            />
          )}
        </TitleContainer>
      )}
      <TableElt
        className={classes}
        isParentTable={isParentTable}
        isChildTable={isChildTable}
        noHeader={noHeader}
        mobileFirstColumnOnly={mobileFirstColumnOnly}
        firstColumnOnlyBreakpoint={firstColumnOnlyBreakpoint}
      >
        {headersData && tableHeaders}
        {error ? (
          <Row>
            <TableError
              error={{
                ...error,
                pagination: config?.pagination,
              }}
            />
          </Row>
        ) : (
          childrenToDisplay
        )}
      </TableElt>
      {showDefaultPaginator && (
        <DefaultPaginator
          disabled={config?.pagination?.disabled}
          loading={config?.pagination?.loading}
          pagination={config?.pagination ?? { page: 0, pages: 0, per_page: 0 }}
          fetchMore={config?.pagination?.fetchMore}
          onPrevious={config?.pagination?.onPrevious}
          onNext={config?.pagination?.onNext}
          onSubmit={config?.pagination?.onSubmit}
        />
      )}
    </TableContainer>
  ) : null;
};
